import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api, { getEvent, getPlayerState, updatePlayerState, getSuggestions, updateSuggestionStatus, reorderEventParts, createPart, getEventQRCode } from '../utils/api';
import { AudioPlayerManager } from '../utils/audioPlayer';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, User, Check, X, ListMusic, Plus, Music2, CheckCircle, Clock, Edit, LogOut } from 'lucide-react';
import { formatModeLabel } from '../utils/formatters';
import DualDeckPlayer from '../components/DualDeckPlayer';
import QueueManager from '../components/QueueManager';
import AcceptSuggestionDialog from '../components/AcceptSuggestionDialog';
import DJHeader from '../components/dj/DJHeader';
import QueuePanel from '../components/dj/QueuePanel';
import SuggestionsPanel from '../components/dj/SuggestionsPanel';
import HistoryPanel from '../components/dj/HistoryPanel';
import CenterDeckSection from '../components/dj/CenterDeckSection';
import QuickAddTrackDialog from '../components/QuickAddTrackDialog';
import QuickAddPartDialog from '../components/QuickAddPartDialog';
import CurrentPartPlaylist from '../components/CurrentPartPlaylist';
import DraggableQueueItem from '../components/DraggableQueueItem';
import DeckDropZone from '../components/DeckDropZone';
import PlayedTracksHistory from '../components/PlayedTracksHistory';
import NotificationDropdown from '../components/NotificationDropdown';
import ModeSelector from '../components/ModeSelector';
import PartsManager from '../components/PartsManager';
import AddTrackDestinationDialog from '../components/AddTrackDestinationDialog';
import AddPartToQueueDialog from '../components/AddPartToQueueDialog';
import { useFullscreen } from '../hooks/useFullscreen';
import LoadingScreen from '../components/ui/LoadingScreen';
import '../styles/djinterface-responsive.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DJInterface = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const audioPlayerRef = useRef(null);
  
  const [event, setEvent] = useState(null);
  const [playerState, setPlayerState] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [queue, setQueue] = useState([]);
  const [playbackInfo, setPlaybackInfo] = useState({ currentTime: 0, duration: 0, paused: true });
  const [decksInfo, setDecksInfo] = useState(null);
  const [volume, setVolume] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [playerSettings, setPlayerSettings] = useState({
    crossfadeDuration: 5,
    crossfadeCurve: 'logarithmic',
    prefadeStart: 3,
    autoPlayMode: 'continuous'
  });
  const [acceptSuggestionDialog, setAcceptSuggestionDialog] = useState({
    open: false,
    suggestion: null
  });
  const [quickAddTrackOpen, setQuickAddTrackOpen] = useState(false);
  const [quickAddPartOpen, setQuickAddPartOpen] = useState(false);
  const [playedTrackIds, setPlayedTrackIds] = useState([]);
  const [currentPartTracks, setCurrentPartTracks] = useState([]);
  const [activePlaylistId, setActivePlaylistId] = useState(null);
  
  // SMART MIX V2: Pitch info state
  const [pitchInfoA, setPitchInfoA] = useState(null);
  const [pitchInfoB, setPitchInfoB] = useState(null);
  
  // Loop state for each deck
  const [loopSizeA, setLoopSizeA] = useState(null);
  const [loopSizeB, setLoopSizeB] = useState(null);
  
  // Loop-roll state for each deck (temporary loops with slip mode)
  const [loopRollSizeA, setLoopRollSizeA] = useState(null);
  const [loopRollSizeB, setLoopRollSizeB] = useState(null);
  
  // PHASE 8: Track Library removed - local uploads only
  const [showDestinationDialog, setShowDestinationDialog] = useState(false);
  const [pendingUploadDestination, setPendingUploadDestination] = useState(null);
  
  // QR Code modal
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  
  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState({ show: false, current: 0, total: 0 });
  
  // Played tracks history (full objects, not just IDs)
  const [playedTracksHistory, setPlayedTracksHistory] = useState([]);
  
  // Queue search filter
  const [queueSearchQuery, setQueueSearchQuery] = useState('');
  
  // Flag to prevent multiple restorations
  const [hasRestoredState, setHasRestoredState] = useState(false);
  
  // Add Part to Queue dialog
  const [addPartToQueueDialog, setAddPartToQueueDialog] = useState({
    open: false,
    part: null
  });
  
  // Track info for each deck (for display)
  const [deckATrack, setDeckATrack] = useState(null);
  const [deckBTrack, setDeckBTrack] = useState(null);
  
  // Fullscreen hook
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  
  // Calculate total duration of queue
  const queueTotalDuration = useMemo(() => {
    return queue.reduce((total, track) => total + (track.duration_seconds || track.duration || 0), 0);
  }, [queue]);

  // Calculate total duration of played tracks (from history)
  const playedTracksTotalDuration = useMemo(() => {
    return playedTracksHistory.reduce((total, track) => total + (track.duration_seconds || track.duration || 0), 0);
  }, [playedTracksHistory]);

  // Get only played tracks for display (from history)
  const playedTracksOnly = useMemo(() => {
    return playedTracksHistory;
  }, [playedTracksHistory]);

  // Format duration in MM:SS
  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);
  
  // Load QR code when needed
  useEffect(() => {
    if (showQRCode && !qrCodeData && eventId) {
      getEventQRCode(eventId)
        .then(response => {
          console.log('QR Code loaded:', response.data);
          setQrCodeData(response.data);
        })
        .catch(error => {
          console.error('Error loading QR code:', error);
          toast.error('Erreur lors du chargement du QR code');
        });
    }
  }, [showQRCode, qrCodeData, eventId]);
  
  const handleCopyGuestLink = () => {
    if (qrCodeData?.url) {
      navigator.clipboard.writeText(qrCodeData.url);
      toast.success('Lien copié!');
    }
  };
  
  // Refs pour éviter stale closures dans les callbacks audioPlayer
  const eventRef = useRef(event);
  const playerStateRef = useRef(playerState);
  const queueRef = useRef(queue);
  const currentTrackRef = useRef(currentTrack);

  // Keep refs updated with latest state values
  useEffect(() => {
    eventRef.current = event;
    playerStateRef.current = playerState;
    queueRef.current = queue;
    currentTrackRef.current = currentTrack;
  }, [event, playerState, queue, currentTrack]);

  useEffect(() => {
    loadEventData();
    
    // Initialize audio player with dual deck (only if not already initialized)
    if (!audioPlayerRef.current) {
      console.log('🎛️ Initializing AudioPlayer in DJInterface');
      audioPlayerRef.current = new AudioPlayerManager(5); // 5 sec crossfade
    } else {
      console.log('ℹ️ AudioPlayer already initialized, skipping re-initialization');
    }
    
    // Always setup callbacks (they may need updating due to closure changes)
    if (audioPlayerRef.current) {
    
    // Setup all callbacks BEFORE calling init()
    audioPlayerRef.current.onTrackEnd = handleTrackEnd;
    audioPlayerRef.current.onTimeUpdate = (info) => setPlaybackInfo(info);
    audioPlayerRef.current.onDeckChange = (info) => {
      setDecksInfo(audioPlayerRef.current.getDecksInfo());
    };
    
    // Intelligent deck management: auto-preload next track on opposite deck
    audioPlayerRef.current.onTrackPreloaded = (deckId, track) => {
      console.log(`🔄 Auto-loading track on Deck ${deckId}: ${track.title}`);
      
      // Actually load the track on the specified deck
      const audioUrl = track.file_path 
        ? `${BACKEND_URL}/api/audio/${track.file_path}`
        : track.source_url;
      
      if (audioUrl && audioUrl !== '#' && audioPlayerRef.current) {
        audioPlayerRef.current.loadTrackOnDeck(deckId, audioUrl);
        console.log(`✅ Deck ${deckId} armed and ready with: ${track.title}`);
      }
    };

    // Auto-crossfade: triggered when track approaches end in auto/semi-auto mode
    audioPlayerRef.current.onAutoCrossfade = async () => {
      console.log('🎚️ onAutoCrossfade callback triggered!');
      
      // Get fresh state values from refs (avoids stale closure)
      const currentEventData = eventRef.current;
      const currentPlayerState = playerStateRef.current;
      const currentQueue = queueRef.current;
      
      console.log(`📊 Current state (from refs):`, {
        eventParts: currentEventData?.parts?.length || 0,
        currentPartId: currentPlayerState?.current_part_id,
        playerMode: currentPlayerState?.mode,
        queueLength: currentQueue?.length || 0
      });
      
      if (!currentEventData || !currentPlayerState) {
        console.error('❌ Event or PlayerState not available');
        return;
      }
      
      const currentPart = currentEventData.parts?.find(p => p.id === currentPlayerState.current_part_id);
      const partMode = currentPart?.mode;
      // Auto-crossfade should work in both 'auto' and 'semi_auto' modes
      const isAutoMode = partMode === 'auto' || partMode === 'semi_auto' || 
                         currentPlayerState.mode === 'auto' || currentPlayerState.mode === 'semi_auto';
      
      console.log(`🎚️ Auto-crossfade check | Part: ${currentPart?.name} | Mode: ${partMode} | IsAuto: ${isAutoMode}`);
      
      if (isAutoMode) {
        if (currentQueue && currentQueue.length > 0) {
          console.log(`✅ Mode AUTO + Queue available (${currentQueue.length}) → Triggering handleNext()`);
          
          // HISTORIZE current track BEFORE crossfade
          const currentTrackData = currentTrackRef.current;
          if (currentTrackData && currentTrackData.id) {
            console.log(`📝 Auto-historizing track before crossfade: ${currentTrackData.title}`);
            addToPlayedHistory(currentTrackData);
          }
          
          // Trigger automatic crossfade
          await handleNext();
        } else {
          console.log(`⏸️ Mode AUTO but Queue empty - cannot auto-crossfade`);
          toast.info('Fin de la playlist');
        }
      } else {
        console.log(`⏸️ Mode MANUAL (${partMode}) - auto-crossfade disabled`);
      }
    };

    // Deck started: preload next track immediately on opposite deck
    audioPlayerRef.current.onDeckStarted = (deckId) => {
      console.log(`🎬 Deck ${deckId} started playing - preloading next on opposite deck`);
      
      const oppositeDeck = deckId === 'A' ? 'B' : 'A';
      
      // Check if opposite deck already has a track loaded
      const decksInfo = audioPlayerRef.current.getDecksInfo();
      const oppositeDeckInfo = oppositeDeck === 'A' ? decksInfo.deckA : decksInfo.deckB;
      
      // Use queueRef to avoid stale closure
      const currentQueue = queueRef.current;
      
      // If opposite deck is empty and we have tracks in queue, preload immediately
      if (!oppositeDeckInfo && currentQueue && currentQueue.length > 0) {
        const nextTrack = currentQueue[0];
        console.log(`🔄 Immediate preload on Deck ${oppositeDeck}: ${nextTrack.title}`);
        
        const audioUrl = nextTrack.file_path 
          ? `${BACKEND_URL}/api/audio/${nextTrack.file_path}`
          : nextTrack.source_url;
        
        if (audioUrl && audioUrl !== '#' && audioPlayerRef.current) {
          audioPlayerRef.current.loadTrackOnDeck(oppositeDeck, audioUrl, nextTrack.bpm);
          console.log(`✅ Deck ${oppositeDeck} armed with next track`);
        }
      } else if (oppositeDeckInfo) {
        console.log(`ℹ️ Deck ${oppositeDeck} already loaded`);
      } else {
        console.log(`ℹ️ Queue empty - nothing to preload`);
      }
    };

      // Initialize the audio player NOW (after all callbacks are set)
      // ALWAYS call init() - it handles clearing old intervals internally
      console.log('✅ All callbacks set, calling audioPlayer.init()');
      audioPlayerRef.current.init();
    }

    // Update decks info regularly (optimized: 500ms for better performance)
    const interval = setInterval(() => {
      if (audioPlayerRef.current) {
        // Use requestAnimationFrame to sync with browser repaint (no lag)
        requestAnimationFrame(() => {
          setDecksInfo(audioPlayerRef.current.getDecksInfo());
          // Smart Mix v2: Update pitch info for both decks
          setPitchInfoA(audioPlayerRef.current.getPitchInfo('A'));
          setPitchInfoB(audioPlayerRef.current.getPitchInfo('B'));
        });
      }
    }, 500); // Reduced from 250ms to 500ms for better performance (no lag)

    return () => {
      clearInterval(interval);
      if (audioPlayerRef.current) {
        audioPlayerRef.current.stop();
      }
    };
  }, [eventId]);

  // Real-time suggestions via frequent polling (2s interval for near real-time)
  useEffect(() => {
    if (!eventId) return;
    
    // Initial load
    const loadSuggestions = async () => {
      try {
        const response = await getSuggestions(eventId);
        setSuggestions(response.data);
      } catch (error) {
        console.error('Error loading suggestions:', error);
      }
    };
    
    loadSuggestions();
    
    // Poll suggestions every 5 seconds (reduced from 2s to prevent lag)
    const pollInterval = setInterval(async () => {
      try {
        const response = await getSuggestions(eventId);
        const newSuggestions = response.data;
        
        // Update suggestions state
        setSuggestions(prevSuggestions => {
          // Compare with previous state to detect new suggestions
          const oldPendingCount = prevSuggestions.filter(s => s.status === 'pending').length;
          const newPendingCount = newSuggestions.filter(s => s.status === 'pending').length;
          
          if (newPendingCount > oldPendingCount) {
            console.log(`🎵 New suggestion received! (${newPendingCount - oldPendingCount} new)`);
          }
          
          return newSuggestions;
        });
      } catch (error) {
        console.error('Error polling suggestions:', error);
      }
    }, 5000); // 5 seconds (reduced from 2s to prevent lag)

    return () => {
      clearInterval(pollInterval);
    };
  }, [eventId]);

  const loadEventData = async () => {
    try {
      const [eventRes, playerRes, suggestionsRes] = await Promise.all([
        getEvent(eventId),
        getPlayerState(eventId),
        getSuggestions(eventId)
      ]);
      
      setEvent(eventRes.data);
      setPlayerState(playerRes.data);
      setSuggestions(suggestionsRes.data);
      
      // Load current track if playing
      if (playerRes.data.current_track_id) {
        await loadTrackDetails(playerRes.data.current_track_id);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadTrackDetails = async (trackId) => {
    try {
      const response = await api.get(`/tracks/${trackId}`);
      if (response.data) {
        setCurrentTrack(response.data);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.warn(`⚠️ Track ${trackId} not found (404) - may have been deleted`);
      } else {
        console.error('Error loading track:', error);
      }
      // Track not found or error - ignore silently
    }
  };

  const handlePlayPause = async (deckId = null) => {
    if (!audioPlayerRef.current) return;

    const currentMode = activePart?.mode || 'auto';

    // If no deck specified, use active deck or start from current part
    if (!deckId) {
      if (playbackInfo.paused) {
        if (currentTrack) {
          audioPlayerRef.current.resume();
        } else {
          // Start playing from current part
          await startPlayingCurrentPart();
          return;
        }
      } else {
        // Pause with fade-out
        await audioPlayerRef.current.pause(1000);
      }

      await updatePlayerState(eventId, {
        is_playing: !playbackInfo.paused,
        position_seconds: playbackInfo.currentTime
      });
      return;
    }

    // Play/Pause specific deck
    console.log(`🎮 handlePlayPause for Deck ${deckId} (Mode: ${currentMode})`);
    
    // Check if track is loaded on this deck
    const deckInfo = audioPlayerRef.current.getDecksInfo();
    const targetDeck = deckId === 'A' ? deckInfo.deckA : deckInfo.deckB;
    const oppositeDeck = deckId === 'A' ? deckInfo.deckB : deckInfo.deckA;
    const activeDeckId = audioPlayerRef.current.activeDeck;
    
    console.log(`📊 Deck Info:`, {
      targetDeck: targetDeck ? `paused=${targetDeck.paused}` : 'NULL',
      oppositeDeck: oppositeDeck ? `paused=${oppositeDeck.paused}` : 'NULL',
      activeDeckId: activeDeckId || 'NULL',
      mode: currentMode
    });
    
    // MODE MANUEL: Contrôle indépendant des decks
    if (currentMode === 'manual') {
      console.log(`🎛️ MODE MANUEL: Contrôle indépendant du Deck ${deckId}`);
      
      if (!targetDeck) {
        toast.info(`Aucun titre chargé sur Deck ${deckId}`);
        return;
      }
      
      // Toggle play/pause avec fade
      if (targetDeck.paused) {
        await audioPlayerRef.current.playDeck(deckId);
        toast.success(`▶️ Deck ${deckId} en lecture`);
        
        // Historize when starting playback in manual mode
        const trackToHistorize = deckId === 'A' ? deckATrack : deckBTrack;
        if (trackToHistorize) {
          addToPlayedHistory(trackToHistorize);
        }
      } else {
        await audioPlayerRef.current.pauseDeck(deckId, 1000);
        toast.info(`⏸️ Deck ${deckId} en pause`);
      }
      
      return;
    }
    
    // MODE AUTO/SEMI_AUTO: Comportement avec crossfade
    // CRITICAL: If opposite deck is playing and we press Play on this deck → CROSSFADE
    if (oppositeDeck && !oppositeDeck.paused && targetDeck && activeDeckId && deckId !== activeDeckId) {
      const t0 = performance.now();
      console.log(`🎚️ [T+0ms] Play on Deck ${deckId} while Deck ${activeDeckId} playing → CROSSFADE`);
      
      // Trigger crossfade to this deck
      handleNext();
      
      const t1 = performance.now();
      console.log(`⏱️ [T+${(t1-t0).toFixed(2)}ms] handleNext() called (non-blocking)`);
      return;
    }
    
    if (!targetDeck) {
      // No track loaded on this deck yet
      if (deckId === 'A' && currentTrack) {
        // Load current track on Deck A
        const audioUrl = currentTrack.file_path 
          ? `${BACKEND_URL}/api/audio/${currentTrack.file_path}`
          : currentTrack.source_url;
        
        if (audioUrl && audioUrl !== '#') {
          console.log(`🎵 Loading current track on Deck ${deckId}${currentTrack.bpm ? ` (${currentTrack.bpm} BPM)` : ''}`);
          audioPlayerRef.current.loadTrackOnDeck(deckId, audioUrl, currentTrack.bpm);
          // Then play
          await audioPlayerRef.current.playPauseDeck(deckId);
        } else {
          toast.warning('⚠️ Titre de démo - Uploadez de vrais fichiers MP3');
          return;
        }
      } else {
        toast.info(`Aucun titre chargé sur Deck ${deckId}`);
        return;
      }
    } else {
      // Toggle play/pause on this deck with fade
      if (targetDeck.paused) {
        await audioPlayerRef.current.playDeck(deckId);
        
        // Historize when starting playback in auto/semi-auto mode
        const trackToHistorize = deckId === 'A' ? deckATrack : deckBTrack;
        if (trackToHistorize) {
          addToPlayedHistory(trackToHistorize);
        }
      } else {
        await audioPlayerRef.current.pauseDeck(deckId, 1000);
      }
    }

    // Update player state
    const isPlaying = !targetDeck || !targetDeck.paused;
    await updatePlayerState(eventId, {
      is_playing: isPlaying,
      position_seconds: targetDeck?.currentTime || 0
    });
  };

  const startPlayingCurrentPart = async () => {
    if (!playerState?.current_part_id) {
      toast.error('Sélectionnez une partie d\'abord');
      return;
    }

    // Get the current part
    const part = event.parts.find(p => p.id === playerState.current_part_id);
    if (!part) return;

    // Load part playlists
    try {
      const response = await api.get(`/parts/${part.id}`);
      const partData = response.data;
      
      const mainPlaylist = partData.playlists?.find(p => p.type === 'main');
      if (!mainPlaylist) {
        toast.error('Aucune playlist principale trouvée');
        return;
      }

      // Store active playlist ID
      setActivePlaylistId(mainPlaylist.id);

      // Get playlist tracks
      const playlistResponse = await api.get(`/playlists/${mainPlaylist.id}`);
      const playlistData = playlistResponse.data;
      
      if (playlistData.tracks && playlistData.tracks.length > 0) {
        const firstTrack = playlistData.tracks[0];
        await playTrack(firstTrack, playlistData.tracks.slice(1));
      } else {
        toast.error('Aucun titre dans la playlist');
      }
    } catch (error) {
      toast.error('Erreur lors du chargement de la playlist');
    }
  };

  const playTrack = async (track, nextTracks = []) => {
    if (!audioPlayerRef.current || !track) return;

    setCurrentTrack(track);
    setQueue(nextTracks);

    const audioUrl = track.file_path 
      ? `${BACKEND_URL}/api/audio/${track.file_path}`
      : track.source_url;

    if (!audioUrl || audioUrl === '#') {
      toast.error('⚠️ Titre de démo - Uploadez de vrais fichiers MP3 dans l\'éditeur d\'événement');
      return;
    }

    const success = await audioPlayerRef.current.play(audioUrl);
    
    if (success) {
      // Historize track when it starts playing
      addToPlayedHistory(track);
      // Preload next track if available
      if (nextTracks.length > 0) {
        const nextTrack = nextTracks[0];
        const nextUrl = nextTrack.file_path 
          ? `${BACKEND_URL}/api/audio/${nextTrack.file_path}`
          : nextTrack.source_url;
        if (nextUrl && nextUrl !== '#') {
          audioPlayerRef.current.preloadNext(nextUrl);
        }
      }

      await updatePlayerState(eventId, {
        current_track_id: track.id,
        is_playing: true,
        position_seconds: 0
      });
    }
  };

  const handleTrackEnd = async (deckId) => {
    console.log(`🏁 Track ended on Deck ${deckId}`);
    
    // Get fresh state from refs
    const currentEventData = eventRef.current;
    const currentPlayerState = playerStateRef.current;
    const currentQueue = queueRef.current;
    
    // Check if we're in auto mode for current part (includes 'auto' and 'semi_auto')
    const currentPart = currentEventData?.parts?.find(p => p.id === currentPlayerState?.current_part_id);
    const isAutoMode = currentPart?.mode === 'auto' || currentPart?.mode === 'semi_auto' ||
                       currentPlayerState?.mode === 'auto' || currentPlayerState?.mode === 'semi_auto';
    
    console.log(`🤖 Mode: ${isAutoMode ? 'AUTO/SEMI-AUTO' : 'MANUAL'} (part: ${currentPart?.mode})`);
    
    if (!isAutoMode) {
      console.log(`⏸️ Manual mode - stopping after track end`);
      toast.info('Titre terminé - Mode manuel');
      await updatePlayerState(eventId, { is_playing: false });
      return;
    }
    
    // Auto mode: play next track in queue
    if (currentQueue.length > 0) {
      console.log(`🎵 Auto mode: playing next track from queue (${currentQueue.length} remaining)`);
      await handleNext();
    } else {
      // No more tracks in queue - check for backup playlist
      console.log('📋 Queue empty - checking for backup playlist');
      
      if (currentPart && currentEventData) {
        try {
          const partResponse = await api.get(`/parts/${currentPart.id}`);
          const partData = partResponse.data;
          
          const backupPlaylist = partData.playlists?.find(p => p.type === 'backup');
          if (backupPlaylist) {
            console.log('🔄 Loading backup playlist');
            
            const playlistResponse = await api.get(`/playlists/${backupPlaylist.id}`);
            const playlistData = playlistResponse.data;
            
            if (playlistData.tracks && playlistData.tracks.length > 0) {
              setQueue(playlistData.tracks);
              await handleNext();
              toast.info('🔄 Playlist secours activée');
              return;
            }
          }
        } catch (error) {
          console.error('❌ Error loading backup playlist:', error);
        }
      }
      
      // No backup playlist or no tracks
      toast.info('Fin de la partie - Aucun titre suivant');
      await updatePlayerState(eventId, { is_playing: false });
    }
  };

  const handleNext = async () => {
    const tStart = performance.now();
    console.log(`⏭️ [T+0ms] Next button pressed`);
    
    // Use fresh queue from ref
    const currentQueue = queueRef.current || [];
    
    const t1 = performance.now();
    console.log(`📊 [T+${(t1-tStart).toFixed(2)}ms] Queue check: ${currentQueue.length} tracks available`);
    
    if (currentQueue.length === 0) {
      toast.info('Aucun titre suivant dans la file');
      return;
    }

    if (!audioPlayerRef.current) {
      console.error('❌ Audio player not initialized');
      return;
    }

    const [nextTrack, ...remainingQueue] = currentQueue;
    const t2 = performance.now();
    console.log(`🎵 [T+${(t2-tStart).toFixed(2)}ms] Next track: ${nextTrack.title}`);
    
    const nextAudioUrl = nextTrack.file_path 
      ? `${BACKEND_URL}/api/audio/${nextTrack.file_path}`
      : nextTrack.source_url;
    
    if (!nextAudioUrl || nextAudioUrl === '#') {
      toast.error('⚠️ Titre de démo - Uploadez de vrais fichiers MP3');
      // Skip to next in queue
      setQueue(remainingQueue);
      setCurrentTrack(nextTrack);
      return;
    }

    const t3 = performance.now();
    console.log(`⚡ [T+${(t3-tStart).toFixed(2)}ms] About to call crossfadeToNext()`);
    
    // ═══════════════════════════════════════════════════════════════
    // CRITICAL FIX: Start crossfade WITHOUT awaiting
    // ═══════════════════════════════════════════════════════════════
    // Calling setState immediately can block the main thread during fade
    // Solution: Start fade, then update state AFTER a minimal delay
    
    const success = audioPlayerRef.current.crossfadeToNext(nextAudioUrl, remainingQueue, nextTrack.bpm);
    
    const t4 = performance.now();
    console.log(`🚀 [T+${(t4-tStart).toFixed(2)}ms] crossfadeToNext() returned (${(t4-t3).toFixed(2)}ms elapsed)`);
    
    if (success) {
      // Defer React state updates to avoid blocking audio thread
      // Use setTimeout with 0ms to push to next event loop tick
      setTimeout(() => {
        // Mark current track as played and add to history
        const prevTrack = currentTrack;
        if (prevTrack && prevTrack.id) {
          console.log(`✅ Marking track as played: ${prevTrack.id}`);
          setPlayedTrackIds(prev => {
            if (!prev.includes(prevTrack.id)) {
              return [...prev, prevTrack.id];
            }
            return prev;
          });
          
          // Add to played tracks history with timestamp
          setPlayedTracksHistory(prev => {
            // Check if already in history
            if (!prev.find(t => t.id === prevTrack.id)) {
              return [...prev, { ...prevTrack, playedAt: new Date() }];
            }
            return prev;
          });
        }
        
        console.log(`✅ Crossfade successful, updating state:`);
        console.log(`   - New current track: ${nextTrack.title}`);
        console.log(`   - Remaining queue: ${remainingQueue.length} tracks`);
        
        setCurrentTrack(nextTrack);
        setQueue(remainingQueue);

        // Update player state
        updatePlayerState(eventId, {
          current_track_id: nextTrack.id,
          is_playing: true,
          position_seconds: 0,
          next_tracks_queue: remainingQueue.map(t => t.id)
        });

        toast.success(`🎵 ${nextTrack.title}`);
      }, 0); // Push to next event loop tick
      
      // The audioPlayer will automatically preload the next track on the opposite deck
      // via the onTrackPreloaded callback if there are tracks in remainingQueue
    } else {
      toast.error('Erreur lors du crossfade');
    }
  };

  const handleRemoveFromQueue = (trackId) => {
    setQueue(prevQueue => prevQueue.filter(t => t.id !== trackId));
  };

  const handleReorderQueue = (newQueue) => {
    setQueue(newQueue);
  };

  const handleVolumeChange = (value) => {
    const vol = value[0] / 100;
    setVolume(vol);
    if (audioPlayerRef.current) {
      audioPlayerRef.current.setVolume(vol);
    }
  };

  const handleCrossfaderChange = (position) => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.setCrossfader(position);
      setDecksInfo(audioPlayerRef.current.getDecksInfo());
    }
  };

  const handleSettingsChange = (newSettings) => {
    setPlayerSettings(newSettings);
    if (audioPlayerRef.current) {
      audioPlayerRef.current.updateSettings(newSettings);
    }
    toast.success('Paramètres audio enregistrés');
  };

  const handlePitchChange = (deck, value) => {
    console.log(`🎚️ Pitch change Deck ${deck}: ${value}%`);
    if (audioPlayerRef.current) {
      audioPlayerRef.current.setPitchDeck(deck, value);
      
      // Immediately update pitch info for UI
      if (deck === 'A') {
        setPitchInfoA(audioPlayerRef.current.getPitchInfo('A'));
      } else {
        setPitchInfoB(audioPlayerRef.current.getPitchInfo('B'));
      }
    }
  };

  const handleDeckVolumeChange = (deckId, value) => {
    console.log(`🔊 Volume Deck ${deckId}: ${value}%`);
    if (audioPlayerRef.current) {
      audioPlayerRef.current.setDeckVolume(deckId, value / 100);
    }
  };

  // PHASE 5: Hot cues & loops handlers removed

  const handleSeek = (deckId, position) => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.seekDeck(deckId, position);
    }
  };

  const handleCue = (deckId, position) => {
    console.log(`🔄 CUE button pressed for Deck ${deckId}`);
    
    if (!audioPlayerRef.current) return;
    
    // MVP: Simple return to start
    const success = audioPlayerRef.current.cueDeck(deckId);
    
    if (success) {
      toast.success(`🔄 Deck ${deckId} - Retour au début`);
    } else {
      toast.warning(`Aucun titre sur Deck ${deckId}`);
    }
  };

  const handleLoop = (start, end) => {
    console.log(`Loop set: ${start}s - ${end}s`);
    toast.success('Boucle activée');
  };

  const handleSync = (deckId) => {
    console.log(`🔄 Sync button pressed for Deck ${deckId}`);
    
    if (!audioPlayerRef.current) {
      toast.error('Audio player non initialisé');
      return;
    }
    
    const result = audioPlayerRef.current.syncDecks();
    
    if (result.success) {
      toast.success(`✅ ${result.message}`);
      console.log(`✅ Sync successful: ${result.pitchApplied.toFixed(1)}%`);
      
      // Update pitch info for both decks
      setPitchInfoA(audioPlayerRef.current.getPitchInfo('A'));
      setPitchInfoB(audioPlayerRef.current.getPitchInfo('B'));
    } else {
      toast.warning(`⚠️ ${result.message}`);
      console.warn(`⚠️ Sync issue: ${result.message}`);
      
      // Still update pitch info if pitch was clamped
      if (result.actualPitch !== undefined) {
        setPitchInfoA(audioPlayerRef.current.getPitchInfo('A'));
        setPitchInfoB(audioPlayerRef.current.getPitchInfo('B'));
      }
    }
  };

  // Handler for loop changes
  const handleLoopChange = (deckId, size) => {
    if (!audioPlayerRef.current) {
      console.error('❌ AudioPlayer not initialized');
      return;
    }
    
    console.log(`🔁 Loop change requested for Deck ${deckId}: ${size === null ? 'OFF' : size + ' beats'}`);
    
    // Update audioPlayer loop state
    const success = audioPlayerRef.current.setLoop(deckId, size);
    
    if (success) {
      // Update UI state
      if (deckId === 'A') {
        setLoopSizeA(size);
      } else {
        setLoopSizeB(size);
      }
      
      if (size === null) {
        toast.success(`Boucle désactivée (Deck ${deckId})`);
      } else {
        toast.success(`Boucle activée: ${size} temps (Deck ${deckId})`);
      }
    } else {
      toast.error(`Impossible d'activer la boucle sur Deck ${deckId}`);
    }
  };
  
  // Handler for loop-roll start (button press/hold)
  const handleLoopRollStart = (deckId, size) => {
    if (!audioPlayerRef.current) {
      console.error('❌ AudioPlayer not initialized');
      return;
    }
    
    const success = audioPlayerRef.current.activateLoopRoll(deckId, size);
    
    if (success) {
      // Update UI state
      if (deckId === 'A') {
        setLoopRollSizeA(size);
      } else {
        setLoopRollSizeB(size);
      }
    }
  };
  
  // Handler for loop-roll end (button release)
  const handleLoopRollEnd = (deckId) => {
    if (!audioPlayerRef.current) {
      return;
    }
    
    audioPlayerRef.current.deactivateLoopRoll(deckId);
    
    // Update UI state
    if (deckId === 'A') {
      setLoopRollSizeA(null);
    } else {
      setLoopRollSizeB(null);
    }
  };

  // Handler for track BPM updates
  const handleTrackUpdate = (updatedTrack) => {
    // Update the track in the currentPartTracks list
    setQueue(prevQueue => 
      prevQueue.map(track => 
        track.id === updatedTrack.id ? { ...track, ...updatedTrack } : track
      )
    );
    
    // If it's the current track, update it
    if (currentTrack?.id === updatedTrack.id) {
      setCurrentTrack(prev => ({ ...prev, ...updatedTrack }));
    }
    
    console.log(`✅ Track updated: ${updatedTrack.title} - BPM: ${updatedTrack.bpm}`);
  };

  const handleActivatePart = async (partId) => {
    try {
      console.log(`🎯 Activating part: ${partId}`);
      
      // Find the part
      const part = event.parts.find(p => p.id === partId);
      if (!part) {
        toast.error('Partie non trouvée');
        return;
      }

      // Load part with playlists
      const partResponse = await api.get(`/parts/${partId}`);
      const partData = partResponse.data;
      
      console.log(`📋 Part data loaded:`, partData);

      // Find main playlist
      const mainPlaylist = partData.playlists?.find(p => p.type === 'main');
      if (!mainPlaylist) {
        toast.warning('Aucune playlist principale - partie activée sans track');
        setActivePlaylistId(null);
        await updatePlayerState(eventId, {
          current_part_id: partId,
          is_playing: false,
          position_seconds: 0,
          mode: part.mode || 'auto'
        });
        setPlayerState({ ...playerState, current_part_id: partId, mode: part.mode || 'auto' });
        return;
      }

      // Store active playlist ID for uploads
      setActivePlaylistId(mainPlaylist.id);

      // Get playlist tracks
      const playlistResponse = await api.get(`/playlists/${mainPlaylist.id}`);
      const playlistData = playlistResponse.data;
      
      console.log(`🎵 Playlist tracks:`, playlistData.tracks?.length || 0);

      if (playlistData.tracks && playlistData.tracks.length > 0) {
        const firstTrack = playlistData.tracks[0];
        const remainingTracks = playlistData.tracks.slice(1);
        
        console.log(`✅ Auto-loading first track on Deck A: ${firstTrack.title}`);
        
        // Store all tracks from this part for playlist display
        setCurrentPartTracks(playlistData.tracks);
        
        // Load first track on Deck A
        setCurrentTrack(firstTrack);
        setQueue(remainingTracks);

        // Update player state
        await updatePlayerState(eventId, {
          current_part_id: partId,
          current_track_id: firstTrack.id,
          is_playing: false,
          position_seconds: 0,
          mode: part.mode || 'auto',
          next_tracks_queue: remainingTracks.map(t => t.id)
        });

        setPlayerState({ 
          ...playerState, 
          current_part_id: partId, 
          current_track_id: firstTrack.id,
          mode: part.mode || 'auto',
          next_tracks_queue: remainingTracks.map(t => t.id)
        });

        // Preload audio on Deck A (but don't play yet)
        if (audioPlayerRef.current) {
          const audioUrl = firstTrack.file_path 
            ? `${BACKEND_URL}/api/audio/${firstTrack.file_path}`
            : firstTrack.source_url;
          
          if (audioUrl && audioUrl !== '#') {
            // Actually load the track on Deck A with BPM
            console.log(`🎧 Loading track on Deck A: ${audioUrl}${firstTrack.bpm ? ` (${firstTrack.bpm} BPM)` : ''}`);
            audioPlayerRef.current.loadTrackOnDeck('A', audioUrl, firstTrack.bpm);
            
            // IMMEDIATE PRELOAD: Also load next track on Deck B for instant readiness
            if (remainingTracks.length > 0) {
              const secondTrack = remainingTracks[0];
              const secondAudioUrl = secondTrack.file_path 
                ? `${BACKEND_URL}/api/audio/${secondTrack.file_path}`
                : secondTrack.source_url;
              
              if (secondAudioUrl && secondAudioUrl !== '#') {
                console.log(`🔄 Preloading second track on Deck B: ${secondTrack.title}${secondTrack.bpm ? ` (${secondTrack.bpm} BPM)` : ''}`);
                audioPlayerRef.current.loadTrackOnDeck('B', secondAudioUrl, secondTrack.bpm);
              }
            }
          } else {
            toast.warning('⚠️ Titre de démo - Uploadez de vrais fichiers MP3');
          }
        }

        toast.success(`Partie activée : ${part.name} | Premier titre chargé sur Deck A`);
      } else {
        // No tracks in main playlist, try backup
        const backupPlaylist = partData.playlists?.find(p => p.type === 'backup');
        if (backupPlaylist) {
          toast.info('Playlist principale vide - utilisez la playlist secours');
        } else {
          toast.warning('Aucun titre dans cette partie');
        }
        
        await updatePlayerState(eventId, {
          current_part_id: partId,
          is_playing: false,
          position_seconds: 0,
          mode: part.mode || 'auto'
        });
        setPlayerState({ ...playerState, current_part_id: partId, mode: part.mode || 'auto' });
      }
    } catch (error) {
      console.error('❌ Error activating part:', error);
      toast.error('Erreur lors de l\'activation');
    }
  };

  const handleSuggestionAction = async (suggestionId, status) => {
    try {
      const suggestion = suggestions.find(s => s.id === suggestionId);
      
      if (status === 'accepted' && suggestion) {
        console.log(`✅ Opening accept dialog for suggestion: ${suggestion.requested_title}`);
        
        // Open dialog to upload/select track for this suggestion
        setAcceptSuggestionDialog({
          open: true,
          suggestion: suggestion
        });
      } else if (status === 'rejected') {
        // Reject directly
        await updateSuggestionStatus(suggestionId, status);
        setSuggestions(suggestions.map(s => 
          s.id === suggestionId ? { ...s, status } : s
        ));
        toast.success('Suggestion refusée');
      }
    } catch (error) {
      console.error('❌ Error handling suggestion:', error);
      toast.error('Erreur');
    }
  };

  const handleAcceptSuggestionSuccess = async (uploadedTrack, suggestionId) => {
    console.log(`✅ Suggestion accepted with track:`, uploadedTrack);
    
    try {
      // Update suggestion status to accepted
      await updateSuggestionStatus(suggestionId, 'accepted');
      setSuggestions(suggestions.map(s => 
        s.id === suggestionId ? { ...s, status: 'accepted' } : s
      ));
      
      // Add uploaded track to queue
      setQueue(prev => [...prev, uploadedTrack]);
      
      toast.success(`🎵 Suggestion acceptée et ajoutée à la file !`);
    } catch (error) {
      console.error('❌ Error updating suggestion status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleQuickAddTrackSuccess = (uploadedTracks, destination) => {
    console.log(`✅ Quick add success: ${uploadedTracks.length} tracks to ${destination}`);
    
    if (destination === 'queue') {
      // Add to queue
      setQueue(prev => [...prev, ...uploadedTracks]);
    } else {
      // Added to playlist - just reload event to show new tracks
      loadEventData();
    }
  };

  const handleQuickAddPartSuccess = (newPart) => {
    console.log(`✅ New part created:`, newPart);
    
    // Reload event to show new part
    loadEventData();
  };

  const handleDeckDrop = async (deckId, track, queueIndex, modifiers = {}) => {
    console.log(`🎯 Dropped track "${track.title}" on Deck ${deckId}`, modifiers);
    
    if (!audioPlayerRef.current) return;
    
    // Check if deck is currently playing
    const deckInfo = audioPlayerRef.current.getDecksInfo();
    const targetDeckInfo = deckId === 'A' ? deckInfo.deckA : deckInfo.deckB;
    const isPlaying = targetDeckInfo && !targetDeckInfo.paused;
    
    if (isPlaying) {
      toast.warning(`Deck ${deckId} est en lecture - impossible de charger un nouveau titre`);
      return;
    }
    
    // Load track on the specified deck
    const audioUrl = track.file_path 
      ? `${BACKEND_URL}/api/audio/${track.file_path}`
      : track.source_url;
    
    if (audioUrl && audioUrl !== '#' && audioPlayerRef.current) {
      audioPlayerRef.current.loadTrackOnDeck(deckId, audioUrl, track.bpm);
      
      // Handle queue modification based on modifier keys
      const newQueue = [...queue];
      
      if (modifiers.shift) {
        // Shift+Drop: "Play Next" - insert at position 0 (after current)
        const trackIndex = newQueue.findIndex(t => t.id === track.id);
        if (trackIndex > -1) {
          newQueue.splice(trackIndex, 1);
        }
        newQueue.unshift(track);
        toast.success(`🎵 ${track.title} chargé sur Deck ${deckId} - Jouer ensuite`);
      } else {
        // Normal drop: remove from queue and prioritize
        const trackIndex = newQueue.findIndex(t => t.id === track.id);
        if (trackIndex > -1) {
          newQueue.splice(trackIndex, 1);
        }
        newQueue.unshift(track);
        toast.success(`🎵 ${track.title} chargé sur Deck ${deckId} et priorisé`);
      }
      
      setQueue(newQueue);
    } else {
      toast.error('⚠️ Titre de démo - Uploadez de vrais fichiers MP3');
    }
  };

  /**
   * Load a track from queue to specified deck with smart behavior
   * - Loads at 8 seconds automatically (handled by audioPlayer)
   * - If loading on PLAYING deck: crossfade smoothly to new track
   * - If loading on OPPOSITE deck: load and pause (ready for next crossfade)
   */
  const handleLoadQueueTrackToDeck = useCallback(async (track, deckId) => {
    console.log(`🎧 Loading "${track.title}" to Deck ${deckId}`);
    
    if (!audioPlayerRef.current) {
      toast.error('Lecteur audio non initialisé');
      return;
    }
    
    // Get current deck status
    const deckInfo = audioPlayerRef.current.getDecksInfo();
    const targetDeckInfo = deckId === 'A' ? deckInfo.deckA : deckInfo.deckB;
    const oppositeDeckInfo = deckId === 'A' ? deckInfo.deckB : deckInfo.deckA;
    
    const isTargetPlaying = targetDeckInfo && !targetDeckInfo.paused;
    const isOppositePlaying = oppositeDeckInfo && !oppositeDeckInfo.paused;
    
    // Build audio URL
    const audioUrl = track.file_path 
      ? `${BACKEND_URL}/api/audio/${track.file_path}`
      : track.source_url;
    
    if (!audioUrl || audioUrl === '#') {
      toast.error('⚠️ Titre de démo - Uploadez de vrais fichiers MP3');
      return;
    }
    
    // Load track on deck (positioned at 8s automatically)
    audioPlayerRef.current.loadTrackOnDeck(deckId, audioUrl, track.bpm);
    
    // Wait for track to load
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (isTargetPlaying) {
      // Loading on PLAYING deck: crossfade smoothly to new track
      toast.info(`🔀 Crossfade vers "${track.title}" sur Deck ${deckId}...`);
      
      // Start playing the new track
      const newDeckAudio = deckId === 'A' ? audioPlayerRef.current.deckA : audioPlayerRef.current.deckB;
      if (newDeckAudio) {
        newDeckAudio.play();
        
        // Perform smooth crossfade over 3 seconds
        const targetCrossfaderPosition = deckId === 'A' ? -1 : 1;
        audioPlayerRef.current.setCrossfader(targetCrossfaderPosition, true);
        
        // Historize when starting playback
        addToPlayedHistory(track);
        
        toast.success(`✅ "${track.title}" en lecture sur Deck ${deckId}`);
      }
    } else if (isOppositePlaying) {
      // Loading on OPPOSITE deck while other is playing: load and keep paused
      toast.success(`✅ "${track.title}" prêt sur Deck ${deckId} (en attente du crossfade automatique)`);
      // Don't play it - it will auto-play when the opposite deck triggers auto-crossfade
      // Will be historized when it actually starts playing
    } else {
      // Both decks stopped: just load it ready
      toast.success(`✅ "${track.title}" chargé sur Deck ${deckId} (prêt à 8s)`);
      // Will be historized when user presses play
    }
    
    // Update track info in state for display (force refresh)
    if (deckId === 'A') {
      setDeckATrack(track);
    } else {
      setDeckBTrack(track);
    }
    
    // Update queue - remove the loaded track
    const updatedQueue = queue.filter(t => t.id !== track.id);
    setQueue(updatedQueue);
    
    // PRÉCHARGEMENT INTELLIGENT: Précharger la prochaine track sur le deck opposé
    if (updatedQueue.length > 0) {
      const nextTrack = updatedQueue[0];
      const oppositeDeckId = deckId === 'A' ? 'B' : 'A';
      
      // Si le deck opposé n'est pas en lecture, précharger
      const oppositeDeckInfo = deckId === 'A' ? deckInfo.deckB : deckInfo.deckA;
      const isOppositePlayingOrLoaded = oppositeDeckInfo && oppositeDeckInfo.duration > 0;
      
      if (!isOppositePlayingOrLoaded) {
        console.log(`🔄 Préchargement de "${nextTrack.title}" sur Deck ${oppositeDeckId} (position 8s)`);
        const nextAudioUrl = nextTrack.file_path 
          ? `${BACKEND_URL}/api/audio/${nextTrack.file_path}`
          : nextTrack.source_url;
        
        if (nextAudioUrl && nextAudioUrl !== '#') {
          // Charger la track sur le deck opposé (à 8s, en pause)
          setTimeout(() => {
            audioPlayerRef.current?.loadTrackOnDeck(oppositeDeckId, nextAudioUrl, nextTrack.bpm);
            
            // Mettre à jour l'affichage
            if (oppositeDeckId === 'A') {
              setDeckATrack(nextTrack);
            } else {
              setDeckBTrack(nextTrack);
            }
            
            toast.info(`🎵 "${nextTrack.title}" préchargé sur Deck ${oppositeDeckId}`, { duration: 2000 });
          }, 1000); // Attendre 1s après le chargement du deck principal
        }
      }
    }
    
  }, [BACKEND_URL, queue]);

  // Historize track when it starts playing (called from various places)
  const addToPlayedHistory = useCallback((track) => {
    if (!track || !track.id) return;
    
    console.log(`📝 Historizing track: ${track.title}`);
    
    // Add to played track IDs
    setPlayedTrackIds(prev => {
      if (!prev.includes(track.id)) {
        return [...prev, track.id];
      }
      return prev;
    });
    
    // Add to played tracks history with timestamp
    setPlayedTracksHistory(prev => {
      // Check if already in history
      if (!prev.find(t => t.id === track.id)) {
        return [...prev, { ...track, playedAt: new Date() }];
      }
      return prev;
    });
  }, []);

  const handleModeChange = async (newMode) => {
    if (!playerState?.current_part_id) {
      toast.error('Aucune partie active');
      return;
    }
    
    console.log(`🔄 Changing mode to: ${newMode}`);
    
    try {
      // Update player state mode
      await updatePlayerState(eventId, {
        mode: newMode
      });
      
      setPlayerState(prev => ({ ...prev, mode: newMode }));
      
      // Also update the part mode
      await api.patch(
        `/parts/${playerState.current_part_id}`,
        { mode: newMode }
      );
      
      toast.success(`Mode changé : ${formatModeLabel(newMode)}`);
      
      // Reload event to reflect changes
      loadEventData();
    } catch (error) {
      console.error('❌ Error changing mode:', error);
      toast.error('Erreur lors du changement de mode');
    }
  };

  const scrollToSuggestions = () => {
    const suggestionsElement = document.getElementById('suggestions-section');
    if (suggestionsElement) {
      suggestionsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // PHASE 8: Parts reordering
  const handleReorderParts = useCallback(async (draggedPartId, targetPartId) => {
    if (!event?.parts) return;
    
    const draggedIndex = event.parts.findIndex(p => p.id === draggedPartId);
    const targetIndex = event.parts.findIndex(p => p.id === targetPartId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    // Optimistic UI update
    const newParts = [...event.parts];
    const [draggedPart] = newParts.splice(draggedIndex, 1);
    newParts.splice(targetIndex, 0, draggedPart);
    
    setEvent(prev => ({ ...prev, parts: newParts }));
    
    // Persist to backend
    try {
      const newOrder = newParts.map(p => p.id);
      await reorderEventParts(eventId, newOrder);
      toast.success('Ordre des parties enregistré');
    } catch (error) {
      console.error('Error reordering parts:', error);
      toast.error('Erreur lors de la réorganisation');
      // Revert on error
      await loadEvent();
    }
  }, [event, eventId]);

  // Handle part selection/change from PartsManager
  const handlePartChange = useCallback(async (partId) => {
    await handleActivatePart(partId);
  }, [handleActivatePart]);

  // Handle "Add Part to Queue" action
  const handleAddPartToQueue = useCallback((partId) => {
    const part = event?.parts?.find(p => p.id === partId);
    if (!part) return;
    
    // Open dialog to choose how to add
    setAddPartToQueueDialog({ open: true, part });
  }, [event]);

  const handleConfirmAddPartToQueue = useCallback(async (partId, mode) => {
    const part = event?.parts?.find(p => p.id === partId);
    if (!part) {
      console.error('Part not found:', partId);
      return;
    }
    
    console.log(`🎵 Adding part "${part.name}" to queue (mode: ${mode})`);
    
    toast.info(`Ajout de "${part.name}" à la file...`);
    
    // Get all tracks from part's playlists
    try {
      const partTracks = [];
      
      // First, fetch the part with its playlists from the API
      console.log(`📋 Fetching part data for ${partId}...`);
      const partResponse = await axios.get(`${BACKEND_URL}/api/parts/${partId}`, {
        
      });
      
      const partWithPlaylists = partResponse.data;
      console.log('Part with playlists:', partWithPlaylists);
      
      // Now fetch tracks from each playlist
      if (partWithPlaylists.playlists && partWithPlaylists.playlists.length > 0) {
        for (const playlist of partWithPlaylists.playlists) {
          console.log(`📋 Fetching tracks for playlist ${playlist.id}...`);
          // Fetch playlist tracks
          const response = await axios.get(`${BACKEND_URL}/api/playlists/${playlist.id}`, {
            
          });
          console.log('Playlist response:', response.data);
          
          if (response.data.tracks && response.data.tracks.length > 0) {
            console.log(`✅ Found ${response.data.tracks.length} tracks in playlist`);
            partTracks.push(...response.data.tracks);
          } else {
            console.warn('⚠️ No tracks in playlist response');
          }
        }
      } else {
        console.warn('⚠️ Part has no playlists');
      }
      
      console.log(`📊 Total tracks collected: ${partTracks.length}`);
      
      // Add to queue based on mode
      if (partTracks.length === 0) {
        toast.warning(`Aucun titre trouvé dans "${part.name}"`);
        return;
      }
      
      if (mode === 'replace') {
        setQueue(partTracks);
        toast.success(`File remplacée par ${partTracks.length} titres de "${part.name}"`);
      } else if (mode === 'next') {
        setQueue(prevQueue => [...partTracks, ...prevQueue]);
        toast.success(`${partTracks.length} titres de "${part.name}" ajoutés en priorité`);
      } else {
        // append (default)
        setQueue(prevQueue => [...prevQueue, ...partTracks]);
        toast.success(`${partTracks.length} titres de "${part.name}" ajoutés à la file`);
      }
    } catch (error) {
      console.error('Error adding part to queue:', error);
      toast.error('Erreur lors de l\'ajout');
    }
  }, [event, BACKEND_URL]);

  // Upload destination dialog handlers
  const handleOpenUploadDialog = useCallback(() => {
    // Check for remembered preference
    const remembered = localStorage.getItem('preferredUploadDestination');
    if (remembered) {
      setPendingUploadDestination(remembered);
      // Directly open file picker
      document.getElementById('file-upload-input')?.click();
    } else {
      setShowDestinationDialog(true);
    }
  }, []);

  const handleSelectDestination = useCallback((destination, remember) => {
    setPendingUploadDestination(destination);
    setShowDestinationDialog(false);
    
    // Open file picker
    setTimeout(() => {
      document.getElementById('file-upload-input')?.click();
    }, 100);
  }, []);

  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !pendingUploadDestination) return;

    // Show upload progress
    setUploadProgress({ show: true, current: 0, total: files.length });

    try {
      const uploadedTracks = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress({ show: true, current: i + 1, total: files.length });

        const formData = new FormData();
        formData.append('file', file);
        
        // Determine target playlist
        let playlistId;
        if (pendingUploadDestination === 'queue') {
          // For queue, use current part's playlist
          const currentPart = event?.parts?.find(p => p.id === playerState?.current_part_id);
          playlistId = currentPart?.playlists?.[0]?.id;
        } else {
          // Use specified part's playlist
          const targetPart = event?.parts?.find(p => p.id === pendingUploadDestination);
          playlistId = targetPart?.playlists?.[0]?.id;
        }

        if (!playlistId) {
          throw new Error('Aucune playlist trouvée');
        }

        formData.append('playlist_id', playlistId);

        // Upload file (HttpOnly cookies handle auth automatically)
        const response = await axios.post(`${BACKEND_URL}/api/upload`, formData, {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        uploadedTracks.push(response.data);
      }

      // Route to destination
      if (pendingUploadDestination === 'queue') {
        setQueue(prev => [...prev, ...uploadedTracks]);
        toast.success(`${uploadedTracks.length} titre(s) ajouté(s) à la file`);
      } else {
        const part = event?.parts?.find(p => p.id === pendingUploadDestination);
        toast.success(`${uploadedTracks.length} titre(s) ajouté(s) à "${part?.name}"`);
        loadEventData(); // Refresh
      }

    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadProgress({ show: false, current: 0, total: 0 });
      setPendingUploadDestination(null);
      // Reset input
      e.target.value = '';
    }
  }, [pendingUploadDestination, event, playerState, BACKEND_URL, loadEventData]);

  // PERSISTENCE: Save state to localStorage
  useEffect(() => {
    // Don't save until restoration is complete to avoid overwriting saved state
    if (!eventId || !hasRestoredState) {
      console.log(`⏸️ [SAVE] Skipping save - restoration not complete yet`);
      return;
    }
    
    const stateToSave = {
      queue,
      playedTracksHistory,
      playedTrackIds,
      deckATrack,
      deckBTrack,
      timestamp: new Date().toISOString()
    };
    
    try {
      const serialized = JSON.stringify(stateToSave);
      localStorage.setItem(`djState_${eventId}`, serialized);
      console.log(`💾 [SAVE] State saved:`, {
        queue: queue.length,
        history: playedTracksHistory.length,
        playedIds: playedTrackIds.length,
        deckA: deckATrack?.title || 'none',
        deckB: deckBTrack?.title || 'none',
        size: `${(serialized.length / 1024).toFixed(2)} KB`
      });
    } catch (error) {
      console.error('❌ [SAVE] Error saving state to localStorage:', error);
    }
  }, [queue, playedTracksHistory, playedTrackIds, deckATrack, deckBTrack, eventId, hasRestoredState]);

  // PERSISTENCE: Restore state from localStorage on mount (ONCE)
  useEffect(() => {
    if (!eventId || hasRestoredState) return;
    
    console.log(`🔍 [RESTORE] Attempting to restore state for event ${eventId}`);
    
    try {
      const savedState = localStorage.getItem(`djState_${eventId}`);
      console.log(`📦 [RESTORE] Saved state found:`, savedState ? `${(savedState.length / 1024).toFixed(2)} KB` : 'NULL');
      
      if (savedState) {
        const parsed = JSON.parse(savedState);
        console.log(`📦 [RESTORE] Parsed state:`, {
          queueLength: parsed.queue?.length || 0,
          historyLength: parsed.playedTracksHistory?.length || 0,
          playedIdsLength: parsed.playedTrackIds?.length || 0,
          deckA: parsed.deckATrack?.title || null,
          deckB: parsed.deckBTrack?.title || null,
          timestamp: parsed.timestamp
        });
        
        // Check if state is not too old (less than 24 hours)
        const stateAge = new Date() - new Date(parsed.timestamp);
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        
        // Restore if state has any meaningful data (queue, history, or tracks on decks)
        const hasData = !!(
          (parsed.queue && parsed.queue.length > 0) ||
          (parsed.playedTracksHistory && parsed.playedTracksHistory.length > 0) ||
          (parsed.deckATrack) || 
          (parsed.deckBTrack)
        );
        
        if (stateAge < maxAge && hasData) {
          console.log(`✅ [RESTORE] State is valid, restoring...`);
          // Use setTimeout to ensure state updates don't conflict with initial render
          setTimeout(async () => {
            let restoredCount = 0;
            
            // Validate tracks still exist before restoring
            const validateTrack = async (track) => {
              if (!track || !track.id) return null;
              try {
                await api.get(`/tracks/${track.id}`);
                return track;
              } catch (error) {
                if (error.response?.status === 404) {
                  console.warn(`  ⚠️ [RESTORE] Track "${track.title}" (${track.id}) no longer exists, skipping`);
                  return null;
                }
                return track; // Keep track if error is not 404
              }
            };
            
            if (parsed.queue && parsed.queue.length > 0) {
              console.log(`  📋 [RESTORE] Validating ${parsed.queue.length} tracks in queue...`);
              const validatedQueue = await Promise.all(parsed.queue.map(validateTrack));
              const filteredQueue = validatedQueue.filter(t => t !== null);
              if (filteredQueue.length > 0) {
                console.log(`  📋 [RESTORE] Restoring ${filteredQueue.length}/${parsed.queue.length} valid tracks to queue`);
                setQueue(filteredQueue);
                restoredCount++;
              } else {
                console.log(`  ⚠️ [RESTORE] No valid tracks found in queue`);
              }
            }
            if (parsed.playedTracksHistory && parsed.playedTracksHistory.length > 0) {
              console.log(`  📜 [RESTORE] Restoring ${parsed.playedTracksHistory.length} played tracks`);
              setPlayedTracksHistory(parsed.playedTracksHistory);
              restoredCount++;
            }
            if (parsed.playedTrackIds && parsed.playedTrackIds.length > 0) {
              console.log(`  🆔 [RESTORE] Restoring ${parsed.playedTrackIds.length} played track IDs`);
              setPlayedTrackIds(parsed.playedTrackIds);
              restoredCount++;
            }
            if (parsed.deckATrack) {
              const validDeckA = await validateTrack(parsed.deckATrack);
              if (validDeckA) {
                console.log(`  🎧 [RESTORE] Restoring Deck A: ${validDeckA.title}`);
                setDeckATrack(validDeckA);
                restoredCount++;
              }
            }
            if (parsed.deckBTrack) {
              const validDeckB = await validateTrack(parsed.deckBTrack);
              if (validDeckB) {
                console.log(`  🎧 [RESTORE] Restoring Deck B: ${validDeckB.title}`);
                setDeckBTrack(validDeckB);
                restoredCount++;
              }
            }
            
            console.log(`✅ [RESTORE] Completed: ${restoredCount} items restored`);
            if (restoredCount > 0) {
              toast.success('État restauré depuis la dernière session');
            }
          }, 500); // Small delay to let initial state settle
          
          setHasRestoredState(true);
        } else {
          console.log(`⚠️ [RESTORE] State too old or empty - clearing (age: ${(stateAge / 1000 / 60).toFixed(1)} mins, hasData: ${hasData})`);
          localStorage.removeItem(`djState_${eventId}`);
          setHasRestoredState(true);
        }
      } else {
        console.log(`ℹ️ [RESTORE] No saved state found`);
        setHasRestoredState(true);
      }
    } catch (error) {
      console.error('❌ [RESTORE] Error restoring state:', error);
      setHasRestoredState(true);
    }
  }, [eventId, hasRestoredState]);

  // Reload tracks on decks after restoration
  useEffect(() => {
    if (!hasRestoredState || !audioPlayerRef.current) return;
    
    // Reload Deck A if track was restored
    if (deckATrack && deckATrack.file_path) {
      const audioUrl = `${BACKEND_URL}/api/audio/${deckATrack.file_path}`;
      audioPlayerRef.current.loadTrackOnDeck('A', audioUrl, deckATrack.bpm);
    }
    
    // Reload Deck B if track was restored
    if (deckBTrack && deckBTrack.file_path) {
      const audioUrl = `${BACKEND_URL}/api/audio/${deckBTrack.file_path}`;
      audioPlayerRef.current.loadTrackOnDeck('B', audioUrl, deckBTrack.bpm);
    }
  }, [hasRestoredState, deckATrack, deckBTrack, BACKEND_URL]);

  // Auto-load next part when queue is low (MODE AUTO only)
  useEffect(() => {
    if (!event?.parts || !playerState?.current_part_id) return;
    
    const activePart = event.parts.find(p => p.id === playerState.current_part_id);
    const currentMode = activePart?.mode || 'auto';
    
    if (currentMode !== 'auto') return;
    
    // Si mode AUTO et qu'il reste 2 tracks ou moins dans la queue
    if (queue.length <= 2) {
      const currentPartIndex = event.parts.findIndex(p => p.id === playerState.current_part_id);
      const nextPart = event.parts[currentPartIndex + 1];
      
      if (nextPart && !nextPart._autoLoadTriggered) {
        console.log(`🤖 MODE AUTO: Auto-chargement de la partie "${nextPart.name}" (queue=${queue.length})`);
        
        // Marquer comme déjà chargée pour éviter les doubles chargements
        nextPart._autoLoadTriggered = true;
        
        // Charger la partie suivante dans la queue (async)
        (async () => {
          await handleConfirmAddPartToQueue(nextPart.id, 'append');
          toast.info(`🤖 Chargement automatique: "${nextPart.name}"`);
        })();
      }
    }
  }, [queue.length, event, playerState?.current_part_id, handleConfirmAddPartToQueue]);

  // PHASE 8: Track Library handlers
  const handleAddTrackToQueue = (track) => {
    setQueue(prevQueue => [...prevQueue, track]);
  };

  const handleAddTrackToPlaylist = async (track) => {
    if (!playerState?.current_part_id) {
      toast.error('Aucune partie active');
      return;
    }
    
    try {
      // Find the current part's main playlist
      const currentPart = event?.parts?.find(p => p.id === playerState.current_part_id);
      if (!currentPart || !currentPart.playlists || currentPart.playlists.length === 0) {
        toast.error('Aucune playlist trouvée pour cette partie');
        return;
      }
      
      // Get main playlist (first one)
      const mainPlaylistId = currentPart.playlists[0].id;
      
      // Add track to playlist via API
      await axios.post(`${BACKEND_URL}/api/playlists/${mainPlaylistId}/tracks`, {
        track_id: track.id
      });
      
      toast.success(`"${track.title}" ajouté à la playlist`);
      
      // Refresh current part tracks
      loadCurrentPartTracks(playerState.current_part_id);
    } catch (error) {
      console.error('Error adding track to playlist:', error);
      toast.error('Erreur lors de l\'ajout à la playlist');
    }
  };

  if (loading) {
    return <LoadingScreen message="Préparation de l'interface DJ..." />;
  }

  // Block access if event is ended
  if (event?.status === 'ended') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark via-dark-card to-dark flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">✓</div>
          <h2 className="text-2xl font-bold text-white mb-2">Événement terminé</h2>
          <p className="text-sunset-orange/70 mb-6">Cet événement est terminé et l'interface DJ n'est plus accessible.</p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-gradient-to-r from-sunset-orange to-sunset-secondary hover:from-purple-700 hover:to-blue-700 text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au tableau de bord
          </Button>
        </div>
      </div>
    );
  }

  const pendingSuggestions = suggestions.filter(s => s.status === 'pending');
  const activePart = event?.parts?.find(p => p.id === playerState?.current_part_id);
  const currentTrackBpm = currentTrack?.bpm || null;

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#1F2937] p-2 sm:p-4"
      role="main"
      aria-label="Interface DJ"
    >
      <div className="max-w-[1800px] mx-auto">
        {/* Header - Refactored Component */}
        <DJHeader
          event={event}
          eventId={eventId}
          activePart={activePart}
          playerState={playerState}
          pendingSuggestions={pendingSuggestions}
          isFullscreen={isFullscreen}
          showQRCode={showQRCode}
          onModeChange={handleModeChange}
          onToggleQRCode={() => setShowQRCode(!showQRCode)}
          onToggleFullscreen={toggleFullscreen}
          onSuggestionAction={handleSuggestionAction}
        />

        {/* RESPONSIVE Layout: Stack on mobile, 3-column on desktop */}
        <div className="flex flex-col xl:flex-row gap-2 xl:h-[calc(100vh-100px)]">
          {/* LEFT COLUMN: QUEUE & SUGGESTIONS - Refactored Components */}
          <div className="w-full xl:w-80 2xl:w-96 xl:flex-shrink-0 flex flex-col gap-2 min-h-0 order-2 xl:order-1">
            <QueuePanel
              queue={queue}
              queueTotalDuration={queueTotalDuration}
              queueSearchQuery={queueSearchQuery}
              onQueueSearchChange={setQueueSearchQuery}
              onReorderQueue={handleReorderQueue}
              onRemoveFromQueue={handleRemoveFromQueue}
              onLoadToDeck={handleLoadQueueTrackToDeck}
              onClearQueue={() => setQueue([])}
              onOpenUpload={handleOpenUploadDialog}
              onFileSelect={handleFileSelect}
              formatDuration={formatDuration}
            />
            
            <SuggestionsPanel
              pendingSuggestions={pendingSuggestions}
              onAccept={(id) => handleSuggestionAction(id, 'accepted')}
              onReject={(id) => handleSuggestionAction(id, 'rejected')}
            />
          </div>

          {/* CENTER: DUAL DECK PLAYER - Priority on mobile */}
          <div className="flex-1 min-w-0 order-1 xl:order-2">
            <CenterDeckSection
              activePart={activePart}
              playedTracksCount={playedTracksOnly.length}
              playedTracksTotalDuration={playedTracksTotalDuration}
              formatDuration={formatDuration}
              decksInfo={decksInfo}
              currentTrack={currentTrack}
              nextTrack={queue[0] || null}
              deckATrack={deckATrack}
              deckBTrack={deckBTrack}
              onPlayPause={handlePlayPause}
              onNext={handleNext}
              onVolumeChange={handleVolumeChange}
              onDeckVolumeChange={handleDeckVolumeChange}
              onCrossfaderChange={handleCrossfaderChange}
              onSettingsChange={handleSettingsChange}
              onPitchChange={handlePitchChange}
              onSeek={handleSeek}
              onCue={handleCue}
              onLoop={handleLoop}
              onSync={handleSync}
              onDeckDrop={handleDeckDrop}
              playerSettings={playerSettings}
              volume={volume}
              playbackInfo={playbackInfo}
              pitchInfoA={pitchInfoA}
              pitchInfoB={pitchInfoB}
              loopSizeA={loopSizeA}
              loopSizeB={loopSizeB}
              onLoopChange={handleLoopChange}
              loopRollSizeA={loopRollSizeA}
              loopRollSizeB={loopRollSizeB}
              onLoopRollStart={handleLoopRollStart}
              onLoopRollEnd={handleLoopRollEnd}
            />
          </div>

          {/* RIGHT COLUMN: PARTIES + HISTORY - Stack on mobile */}
          <div className="w-full xl:w-[500px] 2xl:w-[600px] xl:flex-shrink-0 flex flex-col sm:flex-row xl:flex-row gap-2 min-h-0 order-3">
            {/* Parties Manager */}
            <div className="flex-1 min-h-0 sm:min-h-[200px] xl:min-h-0">
              <PartsManager
                parts={event?.parts || []}
                currentPartId={playerState?.current_part_id}
                onSelectPart={handlePartChange}
                onAddPartToQueue={handleAddPartToQueue}
                onReorderParts={handleReorderParts}
                onCreatePart={() => setQuickAddPartOpen(true)}
                className="h-full"
              />
            </div>

            {/* Played Tracks History - Refactored Component */}
            <HistoryPanel
              playedTracks={playedTracksOnly}
              totalDuration={playedTracksTotalDuration}
              formatDuration={formatDuration}
              onClearHistory={() => {
                setPlayedTracksHistory([]);
                setPlayedTrackIds([]);
              }}
            />
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRCode && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowQRCode(false)}
        >
          <div 
            className="bg-dark-card border-2 border-sunset-orange/30 rounded-2xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-text-primary">Inviter des participants</h2>
              <Button
                onClick={() => setShowQRCode(false)}
                variant="ghost"
                size="icon"
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {qrCodeData ? (
              <div className="space-y-4">
                {/* QR Code Image */}
                <div className="bg-white p-4 rounded-lg">
                  <img 
                    src={qrCodeData.qr_code} 
                    alt="QR Code" 
                    className="w-full h-auto"
                  />
                </div>

                {/* Guest Link */}
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Lien invités:</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={qrCodeData.url}
                      readOnly
                      className="flex-1 bg-dark-input border border-sunset-orange/30 rounded px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <Button
                      onClick={handleCopyGuestLink}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copier
                    </Button>
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-dark-input/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400 leading-relaxed">
                    📱 Les invités peuvent scanner ce QR code ou utiliser le lien pour accéder à l'interface de suggestions.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sunset-orange mx-auto"></div>
                <p className="text-sm text-gray-400 mt-2">Chargement du QR code...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Accept Suggestion Dialog */}
      {acceptSuggestionDialog.suggestion && (
        <AcceptSuggestionDialog
          open={acceptSuggestionDialog.open}
          onOpenChange={(open) => setAcceptSuggestionDialog({ open, suggestion: null })}
          suggestion={acceptSuggestionDialog.suggestion}
          playlistId={activePlaylistId} // Use active playlist ID (not part ID!)
          onSuccess={handleAcceptSuggestionSuccess}
        />
      )}

      {/* Quick Add Track Dialog */}
      <QuickAddTrackDialog
        open={quickAddTrackOpen}
        onOpenChange={setQuickAddTrackOpen}
        eventId={eventId}
        currentPartId={playerState?.current_part_id}
        onSuccess={handleQuickAddTrackSuccess}
      />

      {/* Quick Add Part Dialog */}
      <QuickAddPartDialog
        open={quickAddPartOpen}
        onOpenChange={setQuickAddPartOpen}
        eventId={eventId}
        onSuccess={handleQuickAddPartSuccess}
      />

      {/* Add Track Destination Dialog */}
      <AddTrackDestinationDialog
        open={showDestinationDialog}
        onClose={() => setShowDestinationDialog(false)}
        onSelectDestination={handleSelectDestination}
        parts={event?.parts || []}
        currentPartId={playerState?.current_part_id}
      />

      {/* Add Part to Queue Dialog */}
      {addPartToQueueDialog.part && (
        <AddPartToQueueDialog
          open={addPartToQueueDialog.open}
          part={addPartToQueueDialog.part}
          onConfirm={handleConfirmAddPartToQueue}
          onClose={() => setAddPartToQueueDialog({ open: false, part: null })}
        />
      )}
    </div>
  );
};

export default DJInterface;
